import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ClipboardList, Home, Settings, UserRoundSearch, UsersRound } from "lucide-react";
import { Link, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { getBot } from "../../api/chatbotApi";
import { getSchedulingErrorMessage } from "../../api/schedulingApi";
import Header from "../Header";
import WhatsAppIcon from "../WhatsAppIcon";
import useAuth from "../../hooks/useAuth";
import { canAccessAgentConfig, defaultBotSection } from "../../util/routeAccess";

const BotWorkspaceContext = createContext(null);
export const useBotWorkspace = () => useContext(BotWorkspaceContext);

export function BotWorkspaceLanding() {
  const { user } = useAuth();
  const bot = useBotWorkspace();
  const section = defaultBotSection(user, bot?.client_id);
  return section ? <Navigate replace to={section} /> : <p role="alert">No accessible workspace sections.</p>;
}

export function LegacyCustomerWorkspaceRedirect() {
  const { businessId } = useParams();
  return <Navigate replace to={`/agents/${encodeURIComponent(businessId)}/customers`} />;
}

export function LegacyInboxRedirect() {
  const { tenantId, conversationId } = useParams();
  return <Navigate replace to={tenantId
    ? `/agents/${encodeURIComponent(tenantId)}/inbox${conversationId ? `/${encodeURIComponent(conversationId)}` : ""}`
    : "/agents"} />;
}

export function LegacyBotConfigRedirect() {
  const { businessId } = useParams();
  const location = useLocation();
  return <Navigate replace to={`/agents/${encodeURIComponent(businessId)}/agent-config${location.search}${location.hash}`} />;
}

export function LegacyBotsRedirect() {
  const location = useLocation();
  return <Navigate replace to={{
    pathname: location.pathname.replace(/^\/bots?(?=\/|$)/, "/agents"),
    search: location.search,
    hash: location.hash,
  }} state={location.state} />;
}

export default function BotWorkspace() {
  const { user, can } = useAuth();
  const { businessId: routeBusinessId, tenant_id: tenantId } = useParams();
  const businessId = routeBusinessId || tenantId;
  const location = useLocation();
  const [bot, setBot] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const context = useMemo(() => bot ? {
    ...bot,
    updateBot: (updated) => {
      if (updated?.id !== bot.id) throw new Error("Updated bot does not match this workspace.");
      setBot((current) => current?.id === bot.id ? updated : current);
    },
  } : null, [bot]);

  useEffect(() => {
    let active = true;
    setBot(null);
    setError("");
    getBot(businessId).then((response) => {
      const data = response?.data || response;
      const hasClientAccess = user?.memberships?.some((membership) => (
        String(membership.client_id) === String(data?.client_id) && membership.active !== false && !membership.deleted
      ));
      if (!data?.id || String(data.id) !== businessId || data.deleted_at
        || (!user?.is_super_admin && !hasClientAccess)) {
        throw new Error("This bot is unavailable.");
      }
      if (active) setBot(data);
    }).catch((err) => {
      if (active) setError(getSchedulingErrorMessage(err, "Unable to open this bot workspace."));
    });
    return () => { active = false; };
  }, [businessId, attempt, user?.client_id, user?.is_super_admin, user?.memberships]);

  const ready = bot && String(bot.id) === businessId;
  const base = `/agents/${encodeURIComponent(businessId)}`;
  const renderNavigation = (mobile, onNavigate) => (
    <nav aria-label="Bot workspace navigation" className={mobile ? "grid gap-1" : "flex min-w-0 items-center gap-1"}>
      <Link to="/agents" onClick={onNavigate} className="inline-flex min-h-10 items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        title="Back to Home / Change bot" aria-label="Back to Home / Change bot"><Home size={17} />Home</Link>
      {ready && [
        ["customers", "Customers", "customers.read"], ["inbox", "Inbox", "inbox.read"],
        ["prescriptions", "Prescriptions", "prescriptions.read", "prescriptions.create", "prescription_templates.manage"],
        ["leads", "Leads", "leads.read", "lead_discovery.read", "lead_discovery.configure", "lead_outreach.read"],
        ["agent-config", "Agent Config", "bot_configuration.read"]]
        .filter(([path, , ...permissions]) => path === "agent-config"
          ? canAccessAgentConfig(user, bot.client_id)
          : permissions.some((permission) => can(permission, bot.client_id))).map(([path, label]) => {
        const to = `${base}/${path}`;
        const active = location.pathname === to || location.pathname.startsWith(`${to}/`)
          || (path === "agent-config" && location.pathname === `/agents/${encodeURIComponent(businessId)}/meta-config`);
        return <Link key={path} to={to} onClick={onNavigate} aria-current={active ? "page" : undefined}
          className={`inline-flex min-h-10 items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold ${active ? "bg-cyan-50 text-cyan-900" : "text-slate-600 hover:bg-slate-50"}`}>
          {path === "inbox" && <WhatsAppIcon size={17} />}
          {path === "customers" && <UsersRound size={17} aria-hidden="true" />}
          {path === "prescriptions" && <ClipboardList size={17} aria-hidden="true" />}
          {path === "leads" && <UserRoundSearch size={17} aria-hidden="true" />}
          {path === "agent-config" && <Settings size={17} aria-hidden="true" />}
          {label}
        </Link>;
      })}
    </nav>
  );
  return (
    <div>
      <Header renderNavigation={renderNavigation} />
      {error ? <div role="alert" className="space-y-3 text-rose-700">
        <p>{error}</p>
        <button type="button" onClick={() => setAttempt((value) => value + 1)}>Retry loading bot</button>
      </div> : ready ? (
        <BotWorkspaceContext.Provider value={context}>
          <React.Fragment key={businessId}><Outlet /></React.Fragment>
        </BotWorkspaceContext.Provider>
      ) : <p role="status">Loading bot workspace...</p>}
    </div>
  );
}
